import { useEffect, useState, useRef, useMemo } from 'react';

const DailyCompetitionTube = ({ maleCount, femaleCount }) => {
  const total = maleCount + femaleCount;
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const [animProgress, setAnimProgress] = useState(0);

  // ratio = male share. 0.5 = equal, 1 = all male, 0 = all female
  const targetRatio = total === 0 ? 0.5 : maleCount / total;

  useEffect(() => {
    let start = null;
    const duration = 1500;
    const from = 0.5;
    const to = targetRatio;
    const animate = (ts) => {
      if (!start) start = ts;
      const elapsed = ts - start;
      const t = Math.min(elapsed / duration, 1);
      const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      setAnimProgress(from + (to - from) * eased);
      if (t < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [targetRatio]);

  const bubbles = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 25; i++) {
      arr.push({
        x: 0.08 + Math.random() * 0.84,
        size: 2 + Math.random() * 6,
        speed: 0.3 + Math.random() * 0.7,
        delay: Math.random() * 5,
        opacity: 0.15 + Math.random() * 0.4,
        wobble: 0.5 + Math.random() * 2,
      });
    }
    return arr;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    let time = 0;

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      time += 0.016;

      const tubeX = W * 0.1;
      const tubeW = W * 0.8;
      const tubeY = 30;
      const tubeH = H - 60;
      const tubeR = 20;
      const liquidTop = tubeY + tubeH * 0.08;
      const liquidBottom = tubeY + tubeH - 5;
      const liquidH = liquidBottom - liquidTop;

      // Tube glass clip
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(tubeX + tubeR, tubeY);
      ctx.lineTo(tubeX + tubeW - tubeR, tubeY);
      ctx.quadraticCurveTo(tubeX + tubeW, tubeY, tubeX + tubeW, tubeY + tubeR);
      ctx.lineTo(tubeX + tubeW, tubeY + tubeH - tubeR);
      ctx.quadraticCurveTo(tubeX + tubeW, tubeY + tubeH, tubeX + tubeW - tubeR, tubeY + tubeH);
      ctx.lineTo(tubeX + tubeR, tubeY + tubeH);
      ctx.quadraticCurveTo(tubeX, tubeY + tubeH, tubeX, tubeY + tubeH - tubeR);
      ctx.lineTo(tubeX, tubeY + tubeR);
      ctx.quadraticCurveTo(tubeX, tubeY, tubeX + tubeR, tubeY);
      ctx.closePath();
      ctx.clip();

      // Split: female LEFT, male RIGHT
      // When maleRatio grows, splitX moves LEFT → more blue on right
      const splitX = tubeX + tubeW * (1 - animProgress);

      // Pink (female) - LEFT side (from tubeX to splitX)
      const pinkGrad = ctx.createLinearGradient(tubeX, 0, splitX, 0);
      pinkGrad.addColorStop(0, '#d14080');
      pinkGrad.addColorStop(0.5, '#f078aa');
      pinkGrad.addColorStop(1, '#e85d9a');

      // Blue (male) - RIGHT side (from splitX to end)
      const blueGrad = ctx.createLinearGradient(splitX, 0, tubeX + tubeW, 0);
      blueGrad.addColorStop(0, '#4ba3e3');
      blueGrad.addColorStop(0.5, '#2d8fd5');
      blueGrad.addColorStop(1, '#1e6fba');

      // Draw PINK wave (left)
      ctx.beginPath();
      ctx.moveTo(tubeX, liquidBottom);
      ctx.lineTo(tubeX, liquidTop);
      for (let x = tubeX; x <= splitX; x += 2) {
        const nx = (x - tubeX) / tubeW;
        const wave1 = Math.sin(nx * 8 + time * 2.5) * 3;
        const wave2 = Math.sin(nx * 12 - time * 1.8) * 2;
        const wave3 = Math.sin(nx * 5 + time * 3.2) * 1.5;
        ctx.lineTo(x, liquidTop + wave1 + wave2 + wave3);
      }
      ctx.lineTo(splitX, liquidBottom);
      ctx.closePath();
      ctx.fillStyle = pinkGrad;
      ctx.fill();

      // Draw BLUE wave (right)
      ctx.beginPath();
      ctx.moveTo(splitX, liquidBottom);
      for (let x = splitX; x <= tubeX + tubeW; x += 2) {
        const nx = (x - tubeX) / tubeW;
        const wave1 = Math.sin(nx * 8 + time * 2.5) * 3;
        const wave2 = Math.sin(nx * 12 - time * 1.8) * 2;
        const wave3 = Math.sin(nx * 5 + time * 3.2) * 1.5;
        ctx.lineTo(x, liquidTop + wave1 + wave2 + wave3);
      }
      ctx.lineTo(tubeX + tubeW, liquidBottom);
      ctx.closePath();
      ctx.fillStyle = blueGrad;
      ctx.fill();

      // Depth shading
      const depthGrad = ctx.createLinearGradient(0, liquidTop, 0, liquidBottom);
      depthGrad.addColorStop(0, 'rgba(0,0,0,0)');
      depthGrad.addColorStop(0.7, 'rgba(0,0,0,0.04)');
      depthGrad.addColorStop(1, 'rgba(0,0,0,0.1)');
      ctx.fillStyle = depthGrad;
      ctx.fillRect(tubeX, liquidTop, tubeW, liquidH);

      // Caustics
      for (let i = 0; i < 6; i++) {
        const cx = tubeX + tubeW * (0.15 + i * 0.14);
        const cy = liquidTop + liquidH * (0.3 + Math.sin(time * 0.8 + i) * 0.15);
        const cr = 8 + Math.sin(time + i * 2) * 4;
        const causticGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, cr);
        causticGrad.addColorStop(0, 'rgba(255,255,255,0.1)');
        causticGrad.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = causticGrad;
        ctx.beginPath();
        ctx.ellipse(cx, cy, cr * 1.5, cr, Math.sin(time + i) * 0.3, 0, Math.PI * 2);
        ctx.fill();
      }

      // Bubbles
      bubbles.forEach((b) => {
        const elapsed = (time - b.delay + 20) % (5 / b.speed);
        const progress = (elapsed * b.speed) / 5;
        if (progress < 0 || progress > 1) return;
        const bx = tubeX + b.x * tubeW + Math.sin(time * b.wobble + b.delay) * 4;
        const by = liquidBottom - progress * liquidH * 0.85;
        if (by < liquidTop + 5) return;
        const shrink = progress > 0.8 ? 1 - (progress - 0.8) * 5 : 1;
        const r = b.size * shrink;
        if (r <= 0) return;
        const bubbleGrad = ctx.createRadialGradient(bx - r * 0.3, by - r * 0.3, r * 0.1, bx, by, r);
        bubbleGrad.addColorStop(0, `rgba(255,255,255,${b.opacity * 0.8})`);
        bubbleGrad.addColorStop(0.5, `rgba(255,255,255,${b.opacity * 0.3})`);
        bubbleGrad.addColorStop(1, `rgba(255,255,255,${b.opacity * 0.05})`);
        ctx.beginPath();
        ctx.arc(bx, by, r, 0, Math.PI * 2);
        ctx.fillStyle = bubbleGrad;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(bx - r * 0.25, by - r * 0.25, r * 0.3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${b.opacity * 0.7})`;
        ctx.fill();
      });

      // Glass gloss
      const glossGrad = ctx.createLinearGradient(tubeX, 0, tubeX + tubeW, 0);
      glossGrad.addColorStop(0, 'rgba(255,255,255,0.06)');
      glossGrad.addColorStop(0.2, 'rgba(255,255,255,0.12)');
      glossGrad.addColorStop(0.3, 'rgba(255,255,255,0.02)');
      glossGrad.addColorStop(0.7, 'rgba(255,255,255,0)');
      glossGrad.addColorStop(0.85, 'rgba(255,255,255,0.08)');
      glossGrad.addColorStop(1, 'rgba(255,255,255,0.04)');
      ctx.fillStyle = glossGrad;
      ctx.fillRect(tubeX, tubeY, tubeW, tubeH);

      // Vertical shine
      ctx.fillStyle = 'rgba(255,255,255,0.1)';
      ctx.fillRect(tubeX + tubeW * 0.15, tubeY + 10, 2.5, tubeH - 20);

      ctx.restore();

      // Tube border
      ctx.beginPath();
      ctx.moveTo(tubeX + tubeR, tubeY);
      ctx.lineTo(tubeX + tubeW - tubeR, tubeY);
      ctx.quadraticCurveTo(tubeX + tubeW, tubeY, tubeX + tubeW, tubeY + tubeR);
      ctx.lineTo(tubeX + tubeW, tubeY + tubeH - tubeR);
      ctx.quadraticCurveTo(tubeX + tubeW, tubeY + tubeH, tubeX + tubeW - tubeR, tubeY + tubeH);
      ctx.lineTo(tubeX + tubeR, tubeY + tubeH);
      ctx.quadraticCurveTo(tubeX, tubeY + tubeH, tubeX, tubeY + tubeH - tubeR);
      ctx.lineTo(tubeX, tubeY + tubeR);
      ctx.quadraticCurveTo(tubeX, tubeY, tubeX + tubeR, tubeY);
      ctx.closePath();
      ctx.strokeStyle = 'rgba(0,0,0,0.18)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Divider
      if (total > 0) {
        ctx.beginPath();
        ctx.moveTo(splitX, liquidTop - 3);
        ctx.lineTo(splitX, liquidBottom + 3);
        ctx.strokeStyle = 'rgba(255,255,255,0.6)';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([4, 4]);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [animProgress, bubbles, total]);

  const malePercent = total === 0 ? 0 : Math.round((maleCount / total) * 100);
  const femalePercent = total === 0 ? 0 : Math.round((femaleCount / total) * 100);

  return (
    <div className="rounded-xl border bg-card text-card-foreground shadow" data-testid="daily-competition-card">
      {/* Header */}
      <div className="px-6 pt-5 pb-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#3d2a1f]">ميدان التنافس اليومي</h3>
          <span className="text-xs text-muted-foreground font-medium">
            {new Date().toLocaleDateString('ar', { weekday: 'long', day: 'numeric', month: 'short' })}
          </span>
        </div>
      </div>

      {/* Labels: RIGHT = male (blue), LEFT = female (pink) */}
      <div className="flex items-center justify-between px-6 pb-2">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#2d8fd5', boxShadow: '0 0 6px rgba(45,143,213,0.3)' }} />
          <span className="text-sm font-semibold" style={{ color: '#1e6fba' }}>القراء</span>
          <span className="text-lg font-bold" style={{ color: '#1a5a9e' }}>{maleCount}</span>
        </div>
        {total > 0 && (
          <div className="text-center">
            <span className="text-xs text-muted-foreground">المجموع</span>
            <p className="text-sm font-bold text-[#3d2a1f]">{total}</p>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold" style={{ color: '#c23570' }}>{femaleCount}</span>
          <span className="text-sm font-semibold" style={{ color: '#d14080' }}>القارئات</span>
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#f078aa', boxShadow: '0 0 6px rgba(240,120,170,0.3)' }} />
        </div>
      </div>

      {/* Canvas Tube */}
      <div className="px-4 pb-2">
        <canvas
          ref={canvasRef}
          width={600}
          height={140}
          className="w-full"
          style={{ height: '100px' }}
          data-testid="competition-tube-canvas"
        />
      </div>

      {/* Percentage */}
      {total > 0 && (
        <div className="px-6 pb-6">
          <div className="flex items-center justify-between text-xs">
            <span className="font-bold" style={{ color: '#1e6fba' }}>{malePercent}%</span>
            <span className="font-bold" style={{ color: '#d14080' }}>{femalePercent}%</span>
          </div>
        </div>
      )}

      {total === 0 && (
        <div className="px-6 pb-6 text-center">
          <p className="text-sm text-muted-foreground">لم تُسجّل تقارير بعد اليوم... من سيبادر أولاً؟</p>
        </div>
      )}
    </div>
  );
};

export default DailyCompetitionTube;
